/**
 * The four rungs a streak of rehearsals opens, and the arithmetic over them.
 *
 * Counted on `best_streak` and not on the current one, so what is opened stays
 * opened: a reward that can be taken away is a punishment with extra steps, and
 * the streak breaks for a week of flu. The milestones 10/25/50/100 on «Я» are a
 * different count of the same runs and are left alone - they celebrate the run
 * itself, these hand something over.
 *
 * **Gifts are named, never numbered.** «новые глаза — подмигивание», not «глаза
 * №7»: the number is an index into an array and says nothing to the person
 * reading it. The index is in this table because the picker needs it; nothing
 * that reaches a screen prints it.
 *
 * The server enforces the same table in `app/domain/avatar.py` - a client is
 * where the cells are drawn locked, not where the rule lives.
 */

import { SMILE_STEPS, type Smile } from './Avatar';

export type RungKind = 'eyes' | 'color';

export type Rung = {
  /** The streak of rehearsals that opens it. */
  at: number;
  kind: RungKind;
  /** 1-based, as in the API. */
  index: number;
  /** What the person is given, in the words they read. */
  gift: string;
  /** The gift's own name, for a label that has no room for the sentence. */
  name: string;
  /** The number in words, for the sentence on the card that celebrates it. */
  word: string;
};

export const LADDER: Rung[] = [
  {
    at: 5,
    kind: 'eyes',
    index: 7,
    gift: 'новые глаза — подмигивание',
    name: 'подмигивание',
    word: 'Пять',
  },
  { at: 10, kind: 'color', index: 7, gift: 'новый цвет — бирюза', name: 'бирюза', word: 'Десять' },
  {
    at: 15,
    kind: 'eyes',
    index: 8,
    gift: 'новые глаза — взгляд',
    name: 'взгляд',
    word: 'Пятнадцать',
  },
  { at: 20, kind: 'color', index: 8, gift: 'новый цвет — вереск', name: 'вереск', word: 'Двадцать' },
];

/** How many eyes or colours `best` has opened. Six are everybody's from the door. */
export function openCount(kind: RungKind, best: number): number {
  return 6 + LADDER.filter((rung) => rung.kind === kind && best >= rung.at).length;
}

/** Which rung a locked cell is waiting for, or undefined for one of the six. */
export function rungOf(kind: RungKind, index: number): Rung | undefined {
  return LADDER.find((rung) => rung.kind === kind && rung.index === index);
}

/**
 * The rung the person is standing on right now, for the card that celebrates it.
 *
 * Standing on, not «has passed»: nothing about «shown» is stored, and the card
 * is visible exactly while the streak equals the rung - the same rule the
 * milestone card has followed since 2026-08-18. It leaves with the next
 * rehearsal on its own.
 */
export function rungJustReached(streak: number): Rung | undefined {
  return LADDER.find((rung) => rung.at === streak);
}

/** The next thing to reach for, or undefined with the ladder walked. */
export function nextRung(best: number): Rung | undefined {
  return LADDER.find((rung) => best < rung.at);
}

/**
 * The five steps in the case «до …» puts them in.
 *
 * A `Record` and not an if-chain, so that a sixth step - if the spread of this
 * choir ever asks for one - cannot be added without the compiler asking what it
 * is called here. `sad` is in the table for the same reason and is never
 * printed: nothing is ever «до грусти».
 */
const TOWARDS: Record<Smile, string> = {
  wide: 'широкой улыбки',
  smile: 'улыбки',
  half: 'полуулыбки',
  flat: 'ровного рта',
  sad: 'грусти',
};

/**
 * How far the mouth is from the next step up, in the words «Я» prints under the
 * track: «до улыбки ещё 8%», and at the top the reason to stay there.
 *
 * The numbers come from `SMILE_STEPS` rather than being written out again -
 * this line and the mouth itself have to change on the same percentage, and
 * they did not have to when each held its own copy of the ladder.
 */
export function nextSmileLine(pct: number): string {
  // Lowest first: what is wanted is the nearest boundary above, and the table
  // is written highest first because that is the order the mouth is read in.
  const next = [...SMILE_STEPS].reverse().find(([boundary]) => pct < boundary);
  return next === undefined
    ? 'высшая ступень — лицо моргает'
    : `до ${TOWARDS[next[1]]} ещё ${next[0] - pct}%`;
}
